import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  TableChart as TableIcon,
  Refresh as RefreshIcon,
  ArrowBack as BackIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { DATABASE_SERVICE_URL } from '../../services/workerUrls';
import { logService } from '../../services/logService';

interface TableBrowseResponse {
  table: string;
  rows: Record<string, unknown>[];
  totalRows: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export function D1Browser() {
  const [tables, setTables] = useState<string[]>([]);
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [tableData, setTableData] = useState<TableBrowseResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [tablesLoading, setTablesLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dbInfo, setDbInfo] = useState<{ environment: string; database: string } | null>(null);

  // Fetch available tables on mount
  useEffect(() => {
    const fetchTables = async () => {
      try {
        const response = await fetch(`${DATABASE_SERVICE_URL()}/api/db/tables`);
        const json = await response.json();
        if (json.success && json.data) {
          setTables(json.data);
          if (json.meta) {
            setDbInfo(json.meta);
          }
        }
      } catch (err) {
        console.error('Failed to fetch tables:', err);
      } finally {
        setTablesLoading(false);
      }
    };
    fetchTables();
  }, []);

  const fetchTableData = async (tableName: string, offset = 0) => {
    setLoading(true);
    setError(null);

    const logId = logService.db('SELECT', tableName, { offset }, 'pending');
    const startTime = Date.now();

    try {
      const response = await fetch(`${DATABASE_SERVICE_URL()}/api/db/table/${tableName}?limit=50&offset=${offset}`);
      const duration = Date.now() - startTime;
      const json = await response.json();

      if (json.success && json.data) {
        setTableData(json.data);
        logService.updateLog(logId, {
          status: 'success',
          duration,
          details: { rowCount: json.data.rows.length, totalRows: json.data.totalRows }
        });
      } else {
        throw new Error(json.error || 'Failed to fetch data');
      }
    } catch (err) {
      const duration = Date.now() - startTime;
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMsg);
      logService.updateLog(logId, { status: 'error', duration, message: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  const handleTableSelect = (tableName: string) => {
    setSelectedTable(tableName);
    fetchTableData(tableName);
  };

  const handleBack = () => {
    setSelectedTable(null);
    setTableData(null);
    setError(null);
  };

  const handleRefresh = () => {
    if (selectedTable) {
      fetchTableData(selectedTable);
    }
  };

  // Render table list view
  if (!selectedTable) {
    return (
      <Card
        className="panel"
        sx={{
          backgroundColor: 'background.paper',
          border: 1,
          borderColor: 'divider',
        }}
      >
        <CardContent className="panel-header" sx={{ p: 1.5, pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography
              variant="overline"
              sx={{
                color: 'info.main',
                fontWeight: 700,
                letterSpacing: 1,
                fontSize: '0.65rem',
              }}
            >
              D1 Database Browser
            </Typography>
            {dbInfo && (
              <Chip
                label={dbInfo.environment}
                size="small"
                color={dbInfo.environment === 'staging' ? 'warning' : dbInfo.environment === 'production' ? 'error' : 'default'}
                sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600 }}
              />
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" display="block">
            {dbInfo ? dbInfo.database : 'Select a table to browse'}
          </Typography>
        </CardContent>

        <Box className="panel-scroll">
          {tablesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <List dense disablePadding>
              {tables.map((table) => (
                <ListItem key={table} disablePadding>
                  <ListItemButton onClick={() => handleTableSelect(table)}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <TableIcon sx={{ fontSize: 18, color: 'info.main' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={table}
                      primaryTypographyProps={{
                        variant: 'body2',
                        sx: { fontFamily: 'monospace' },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Card>
    );
  }

  // Render table data view
  return (
    <Card
      className="panel"
      sx={{
        backgroundColor: 'background.paper',
        border: 1,
        borderColor: 'divider',
      }}
    >
      <CardContent className="panel-header" sx={{ p: 1.5, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" onClick={handleBack}>
            <BackIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Typography
            variant="subtitle2"
            sx={{ fontFamily: 'monospace', flex: 1 }}
          >
            {selectedTable}
          </Typography>
          <Chip
            label={tableData ? `${tableData.rows.length} of ${tableData.totalRows}` : '...'}
            size="small"
            sx={{ height: 20, fontSize: '0.65rem' }}
          />
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={handleRefresh} disabled={loading}>
              <RefreshIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </CardContent>

      <Box className="panel-scroll" sx={{ px: 1, pb: 1 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}

        {!loading && tableData && tableData.rows.length > 0 && (
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '100%' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      backgroundColor: 'background.paper',
                      whiteSpace: 'nowrap',
                      width: 48,
                    }}
                  >
                    Actions
                  </TableCell>
                  {Object.keys(tableData.rows[0]).map((key) => (
                    <TableCell
                      key={key}
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        backgroundColor: 'background.paper',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {key}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {tableData.rows.map((row, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell sx={{ width: 48, textAlign: 'center' }}>
                      <Tooltip title="Copy row as JSON">
                        <IconButton
                          size="small"
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(JSON.stringify(row, null, 2));
                            } catch (error) {
                              console.error('Failed to copy row:', error);
                            }
                          }}
                          sx={{
                            color: 'primary.main',
                            backgroundColor: 'action.hover',
                            '&:hover': {
                              backgroundColor: 'action.selected',
                            },
                          }}
                        >
                          <ContentCopyIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    {Object.values(row).map((value, cellIdx) => (
                      <TableCell
                        key={cellIdx}
                        sx={{
                          fontSize: '0.7rem',
                          fontFamily: 'monospace',
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {value === null
                          ? <span style={{ color: '#888' }}>null</span>
                          : typeof value === 'object'
                          ? JSON.stringify(value)
                          : String(value)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!loading && tableData && tableData.rows.length === 0 && (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
            No data in this table
          </Typography>
        )}
      </Box>
    </Card>
  );
}
